import React, { useCallback, useEffect, useState } from 'react';
import { appInsights } from '../../../../telemetry/appInsights';
import { useTrackEvent } from '@microsoft/applicationinsights-react-js';
import axios from 'axios';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import MemoryIcon from '@mui/icons-material/Memory';
import LogoutIcon from '@mui/icons-material/Logout';
import GppBadIcon from '@mui/icons-material/GppBad';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  isClearHardwareEnabledForSite,
  isTerminalResetDisabledForSite,
  isThresholdExceededDisabledForSite,
} from '../../../../data/contactCenter/aurora/dropDownOptions';
import {
  formatSignOnStatusLabel,
  formatSignOnTimestamp,
  getEsConnectTerminalId,
  getTerminalDisplayId,
  getTerminalOid,
  validateTerminalAction,
} from '../helpers/utils';
import LastTenTransactionsTab from '../components/lastTenTransactionsTab';
import TerminalHistoryTab from '../components/terminalHistoryTab';
import { getHostEntityStatusCode, getStatusChipMeta } from '../helpers/utils';

const TERMINAL_RESET_ACTION_IDS = new Set(['softReset', 'hardReset']);
const CLEAR_HARDWARE_ACTION_ID = 'clearHardware';
const CLEAR_THRESHOLD_EXCEEDED_ACTION_ID = 'clearThresholdExceeded';

const brand = {
  reachBlue: 'var(--reach-blue)',
  betelOrange: 'var(--betel-orange)',
  arcturusRed: 'var(--arcturus-red)',
  borderSubtle: 'rgba(5, 5, 51, 0.08)',
};

function trimEsConnectTerminalId(esConnectTerminalId) {
  return String(esConnectTerminalId || '').trim();
}

async function getSignOnStatus({ terminalOid, site }) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT_HOST}/signOnStatus`,
      {
        params: {
          terminalId: terminalOid,
          site,
        },
      },
    );
    return response.data?.signOnStatus ?? null;
  } catch (error) {
    console.error('signOnStatus request/parse failed:', error);
    throw error;
  }
}

async function getDownloadStatus({ esConnectTerminalId, site }) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT_HOST}/downloadStatus`,
      {
        params: {
          esConnectTerminalId: trimEsConnectTerminalId(esConnectTerminalId),
          site,
        },
      },
    );
    return response.data?.downloadStatus?.terminalState?.downloadState ?? null;
  } catch (error) {
    console.error('downloadStatus request/parse failed:', error);
    throw error;
  }
}

async function getLastTenTransactions({ terminalOid, site }) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT_HOST}/lastTenTransactions`,
      {
        params: {
          terminalId: terminalOid,
          site,
        },
      },
    );
    const transactions = response.data?.lastTenTransactions;
    if (!transactions) {
      return [];
    }
    const list = Array.isArray(transactions)
      ? transactions
      : typeof transactions === 'object'
        ? Object.values(transactions)
        : [];
    // Some sites (e.g. Georgia) return more than 10; keep UI aligned with "Last 10 Trans."
    return list.slice(0, 10);
  } catch (error) {
    console.error('lastTenTransactions request/parse failed:', error);
    throw error;
  }
}

async function getTerminalHistoryFromApi({ terminalId, site }) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/aurora/terminal/history`,
      {
        params: {
          terminalId,
          site,
        },
      },
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('terminal history request/parse failed:', error);
    throw error;
  }
}

async function terminalSignOff({ terminalOid, site }) {
  try {
    return await axios.post(
      `${process.env.REACT_APP_ENDPOINT_HOST}/terminalSignOff`,
      null,
      {
        params: {
          terminalId: terminalOid,
          site,
        },
      },
    );
  } catch (error) {
    console.error('terminalSignOff request failed:', error);
    throw error;
  }
}

async function securityViolation({ terminalOid, site }) {
  try {
    return await axios.post(
      `${process.env.REACT_APP_ENDPOINT_HOST}/securityViolation`,
      null,
      {
        params: {
          terminalId: terminalOid,
          site,
        },
      },
    );
  } catch (error) {
    console.error('securityViolation request failed:', error);
    throw error;
  }
}

async function thresholdExceeded({ terminalOid, site }) {
  try {
    return await axios.post(
      `${process.env.REACT_APP_ENDPOINT_HOST}/thresholdExceeded`,
      null,
      {
        params: {
          terminalId: terminalOid,
          site,
        },
      },
    );
  } catch (error) {
    console.error('thresholdExceeded request failed:', error);
    throw error;
  }
}

async function freeTicketSequence({ terminalOid, site }) {
  try {
    return await axios.post(
      `${process.env.REACT_APP_ENDPOINT_HOST}/freeTicketSequence`,
      null,
      {
        params: {
          terminalId: terminalOid,
          site,
        },
      },
    );
  } catch (error) {
    console.error('freeTicketSequence request failed:', error);
    throw error;
  }
}

async function terminalReset({ site, esConnectTerminalId, unsoType }) {
  try {
    return await axios.put(
      `${process.env.REACT_APP_ENDPOINT_HOST}/terminalReset`,
      null,
      {
        params: {
          site,
          esConnectTerminalId: trimEsConnectTerminalId(esConnectTerminalId),
          unsoType,
        },
      },
    );
  } catch (error) {
    console.error('terminalReset request failed:', error);
    throw error;
  }
}

async function clearHardwareId({ site, esConnectTerminalId }) {
  try {
    return await axios.delete(
      `${process.env.REACT_APP_ENDPOINT_HOST}/clearHardwareId`,
      {
        params: {
          site,
          esConnectTerminalId: trimEsConnectTerminalId(esConnectTerminalId),
          sapNum: 1,
          hardwareId: '',
        },
      },
    );
  } catch (error) {
    console.error('clearHardwareId request failed:', error);
    throw error;
  }
}

function runTerminalAction(actionId, site, terminal) {
  switch (actionId) {
    case 'softReset':
      return terminalReset({
        site,
        esConnectTerminalId: getEsConnectTerminalId(terminal),
        unsoType: 'SOFT',
      });
    case 'hardReset':
      return terminalReset({
        site,
        esConnectTerminalId: getEsConnectTerminalId(terminal),
        unsoType: 'HARD',
      });
    case 'clearHardware':
      return clearHardwareId({
        site,
        esConnectTerminalId: getEsConnectTerminalId(terminal),
      });
    case 'signOff':
      return terminalSignOff({
        terminalOid: getTerminalOid(terminal),
        site,
      });
    case 'securityViolation':
      return securityViolation({
        terminalOid: getTerminalOid(terminal),
        site,
      });
    case 'clearFreeTicketSequence':
      return freeTicketSequence({
        terminalOid: getTerminalOid(terminal),
        site,
      });
    case 'clearThresholdExceeded':
      return thresholdExceeded({
        terminalOid: getTerminalOid(terminal),
        site,
      });
    default:
      throw new Error(`Unsupported terminal action: ${actionId}`);
  }
}

const TERMINAL_ACTION_GROUP_ORDER = [
  'Maintenance',
  'Administration',
  'Security / Compliance',
];

const TERMINAL_ACTION_DEFINITIONS = [
  {
    id: 'softReset',
    group: 'Maintenance',
    label: 'Soft Reset',
    description: 'Gracefully reconnect terminal',
    icon: RefreshIcon,
    iconColor: 'var(--vega-blue)',
    avatarBg: 'rgba(0, 46, 229, 0.1)',
    confirmVariant: 'contained',
    confirmMuiColor: 'info',
    confirmSx: { bgcolor: 'var(--vega-blue)', color: '#FFFFFF' },
    requiresEsConnect: true,
    confirmTitle: 'Confirm Soft Reset',
    getMessageParts: terminalId => ({
      before: 'Gracefully reconnect terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Soft Reset',
    loadingLabel: 'Soft resetting...',
    success: 'Soft reset completed successfully.',
    error: 'Soft reset failed. Please try again.',
  },
  {
    id: 'hardReset',
    group: 'Maintenance',
    label: 'Hard Reset',
    description: 'Force terminal restart',
    icon: PowerSettingsNewIcon,
    iconColor: 'var(--reach-blue)',
    avatarBg: 'rgba(5, 5, 51, 0.08)',
    confirmVariant: 'contained',
    confirmMuiColor: 'primary',
    confirmSx: {
      bgcolor: 'var(--reach-blue)',
      color: '#FFFFFF',
      fontFamily: 'BrightstarSans-Bold',
    },
    requiresEsConnect: true,
    confirmTitle: 'Confirm Hard Reset',
    getMessageParts: terminalId => ({
      before: 'Force restart terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Hard Reset',
    loadingLabel: 'Hard resetting...',
    success: 'Hard reset completed successfully.',
    error: 'Hard reset failed. Please try again.',
  },
  {
    id: 'clearHardware',
    group: 'Administration',
    label: 'Clear Hardware ID',
    description: 'Remove hardware association',
    icon: MemoryIcon,
    iconColor: 'var(--betel-orange)',
    avatarBg: 'rgba(255, 122, 0, 0.12)',
    confirmVariant: 'contained',
    confirmMuiColor: 'warning',
    confirmSx: { bgcolor: 'var(--betel-orange)', color: '#FFFFFF' },
    requiresEsConnect: true,
    confirmTitle: 'Confirm Clear Hardware ID',
    getMessageParts: terminalId => ({
      before: 'Clear the hardware association for terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Clear Hardware ID',
    loadingLabel: 'Clearing...',
    success: 'Hardware ID cleared successfully.',
    error: 'Clear hardware ID failed. Please try again.',
  },
  {
    id: 'signOff',
    group: 'Administration',
    label: 'Terminal Sign Off',
    description: 'End terminal session',
    icon: LogoutIcon,
    iconColor: 'var(--arcturus-red)',
    avatarBg: 'rgba(231, 52, 67, 0.1)',
    confirmVariant: 'contained',
    confirmMuiColor: 'error',
    confirmSx: { bgcolor: 'var(--arcturus-red)', color: '#FFFFFF' },
    requiresTerminalOid: true,
    confirmTitle: 'Confirm Terminal Sign Off',
    getMessageParts: terminalId => ({
      before: 'Sign off terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Sign Off Terminal',
    loadingLabel: 'Signing off...',
    success: 'Terminal sign off completed successfully.',
    error: 'Terminal sign off failed. Please try again.',
  },
  {
    id: 'securityViolation',
    group: 'Security / Compliance',
    label: 'Security Violation',
    description: 'Clear or acknowledge terminal security violation',
    icon: GppBadIcon,
    iconColor: 'var(--arcturus-red)',
    avatarBg: 'rgba(231, 52, 67, 0.08)',
    confirmVariant: 'outlined',
    confirmMuiColor: 'error',
    confirmSx: {
      borderColor: 'var(--arcturus-red)',
      color: 'var(--arcturus-red)',
      fontFamily: 'BrightstarSans-Regular',
    },
    requiresTerminalOid: true,
    confirmTitle: 'Confirm Security Violation',
    getMessageParts: terminalId => ({
      before: 'Clear or acknowledge the security violation for terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Security Violation',
    loadingLabel: 'Running...',
    success: 'Security violation action completed successfully.',
    error: 'Security violation action failed. Please try again.',
  },
  {
    id: 'clearFreeTicketSequence',
    group: 'Security / Compliance',
    label: 'Clear Free Ticket Sequence',
    description: 'Reset free ticket sequence tracking',
    icon: ConfirmationNumberIcon,
    iconColor: 'var(--canopus-violet)',
    avatarBg: 'rgba(179, 121, 246, 0.12)',
    confirmVariant: 'outlined',
    confirmMuiColor: 'secondary',
    confirmSx: {
      borderColor: 'var(--canopus-violet)',
      color: 'var(--canopus-violet)',
    },
    requiresTerminalOid: true,
    confirmTitle: 'Confirm Clear Free Ticket Sequence',
    getMessageParts: terminalId => ({
      before: 'Clear the free ticket sequence for terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Clear Sequence',
    loadingLabel: 'Clearing...',
    success: 'Free ticket sequence cleared successfully.',
    error: 'Clear free ticket sequence failed. Please try again.',
  },
  {
    id: 'clearThresholdExceeded',
    group: 'Security / Compliance',
    label: 'Clear Threshold Exceeded',
    description: 'Clear threshold exceeded flag',
    icon: ReportProblemIcon,
    iconColor: 'var(--reach-blue)',
    avatarBg: 'rgba(252, 207, 13, 0.25)',
    confirmVariant: 'contained',
    confirmMuiColor: 'warning',
    confirmSx: {
      bgcolor: 'var(--aldebaran-yellow)',
      color: 'var(--reach-blue)',
      fontWeight: 600,
    },
    requiresTerminalOid: true,
    confirmTitle: 'Confirm Clear Threshold Exceeded',
    getMessageParts: terminalId => ({
      before: 'Clear the threshold exceeded flag for terminal ',
      after: '.',
      terminalId,
    }),
    confirmLabel: 'Clear Threshold',
    loadingLabel: 'Clearing...',
    success: 'Threshold exceeded flag cleared successfully.',
    error: 'Clear threshold exceeded failed. Please try again.',
  },
];

/** Display-only; API calls keep the original lowercase site value. */
const formatSiteDisplay = site => {
  if (!site || typeof site !== 'string') return null;
  const trimmed = site.trim();
  if (!trimmed) return null;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

const ConfirmDialog = ({
  open,
  title,
  messageParts,
  onConfirm,
  onCancel,
  loading,
  confirmColor,
  confirmVariant = 'contained',
  confirmLabel,
  loadingLabel,
  confirmButtonSx,
}) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onCancel}
    maxWidth='xs'
    fullWidth
  >
    <DialogTitle sx={{ fontFamily: 'BrightstarSans-Bold', pb: 1 }}>
      <Stack direction='row' spacing={1.5} alignItems='flex-start'>
        <WarningAmberOutlinedIcon color='warning' sx={{ mt: 0.25 }} />
        <span>{title}</span>
      </Stack>
    </DialogTitle>
    <DialogContent sx={{ pt: 0 }}>
      <DialogContentText component='div' sx={{ color: 'text.primary' }}>
        {messageParts?.before}
        <Box
          component='span'
          sx={{ fontFamily: 'BrightstarSans-Bold', fontWeight: 700 }}
        >
          {messageParts?.terminalId}
        </Box>
        {messageParts?.after}
      </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
      <Button variant='text' onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant={confirmVariant}
        color={confirmColor}
        onClick={onConfirm}
        disabled={loading}
        sx={{
          fontFamily: 'BrightstarSans-Regular',
          minWidth: 140,
          ...confirmButtonSx,
        }}
      >
        {loading ? loadingLabel : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

const SIGN_ON_STATUS_DETAIL_FIELDS = [
  { key: 'lastSignonTime', label: 'Last Sign On' },
  { key: 'lastSignoffTime', label: 'Last Sign Off' },
  { key: 'lastActivityTime', label: 'Last Activity' },
  { key: 'lastTellerId', label: 'Last Teller ID' },
];

function getSignOnStatusChipMeta(signonStatus) {
  const label = formatSignOnStatusLabel(signonStatus);
  const normalized = String(signonStatus || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');

  if (
    normalized.includes('SIGNED_ON') ||
    normalized === 'SIGNON' ||
    label.toLowerCase() === 'signed on'
  ) {
    return { label, color: 'success', variant: 'filled' };
  }

  if (
    normalized.includes('SIGNED_OFF') ||
    normalized.includes('SIGN_OFF') ||
    label.toLowerCase() === 'signed off'
  ) {
    return { label, color: 'error', variant: 'filled' };
  }

  return { label, color: 'default', variant: 'outlined' };
}

function getDownloadStateChipMeta(downloadState) {
  if (!downloadState) {
    return null;
  }

  const label = String(downloadState).trim();
  if (!label) {
    return null;
  }

  if (label === 'Ready') {
    return { label, color: 'success', variant: 'filled' };
  }

  return { label, color: 'default', variant: 'outlined' };
}

const InfoSectionTitle = ({ children }) => (
  <Typography
    variant='subtitle2'
    sx={{
      fontFamily: 'BrightstarSans-Bold',
      color: brand.reachBlue,
      fontSize: '0.8rem',
      lineHeight: 1.3,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const DetailRow = ({ label, value }) => (
  <Box>
    <Typography
      variant='caption'
      color='text.secondary'
      display='block'
      sx={{
        mb: 0.2,
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 700,
        color: brand.reachBlue,
        lineHeight: 1.3,
        fontSize: '0.9rem',
        fontFamily: 'BrightstarSans-Bold',
      }}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

const ActivityRow = ({ label, value, valueColor }) => (
  <Typography
    variant='caption'
    sx={{ lineHeight: 1.45, fontSize: '0.75rem', display: 'block' }}
  >
    <Box component='span' sx={{ color: 'text.secondary' }}>
      {`${label}: `}
    </Box>
    <Box
      component='span'
      sx={{
        fontWeight: 700,
        color: valueColor || brand.reachBlue,
        fontFamily: 'BrightstarSans-Bold',
      }}
    >
      {value ?? '—'}
    </Box>
  </Typography>
);

const SignOnActivitySection = ({
  signOnStatus,
  loading,
  downloadState,
  downloadStateLoading,
}) => {
  const signOnChipMeta = signOnStatus?.signonStatus
    ? getSignOnStatusChipMeta(signOnStatus.signonStatus)
    : null;
  const downloadStateChipMeta = getDownloadStateChipMeta(downloadState);

  const getActivityValue = field => {
    const value = signOnStatus?.[field.key];
    if (value == null || value === '') {
      return null;
    }

    return field.key === 'lastTellerId'
      ? String(value)
      : formatSignOnTimestamp(value);
  };

  return (
    <Stack spacing={0.75}>
      <Box>
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          sx={{
            mb: 0.25,
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          Terminal Sign On Status
        </Typography>
        {loading ? (
          <CircularProgress size={16} sx={{ color: 'var(--canopus-violet)' }} />
        ) : signOnChipMeta ? (
          <Chip
            size='small'
            label={signOnChipMeta.label}
            color={signOnChipMeta.color}
            variant={signOnChipMeta.variant}
            sx={{ fontWeight: 600, height: 22 }}
          />
        ) : (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 700,
              color: brand.reachBlue,
              lineHeight: 1.3,
              fontSize: '0.9rem',
              fontFamily: 'BrightstarSans-Bold',
            }}
          >
            —
          </Typography>
        )}
      </Box>
      <Stack spacing={0.35}>
        {SIGN_ON_STATUS_DETAIL_FIELDS.map(field => (
          <ActivityRow
            key={field.key}
            label={field.label}
            value={getActivityValue(field)}
          />
        ))}
      </Stack>
      <Box>
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          sx={{
            mb: 0.25,
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          Download Status
        </Typography>
        {downloadStateLoading ? (
          <CircularProgress size={16} sx={{ color: 'var(--canopus-violet)' }} />
        ) : downloadStateChipMeta ? (
          <Chip
            size='small'
            label={downloadStateChipMeta.label}
            color={downloadStateChipMeta.color}
            variant={downloadStateChipMeta.variant}
            sx={{ fontWeight: 600, height: 22 }}
          />
        ) : (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 700,
              color: brand.reachBlue,
              lineHeight: 1.3,
              fontSize: '0.9rem',
              fontFamily: 'BrightstarSans-Bold',
            }}
          >
            —
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

function isTerminalActionDisabled(action, terminal, site, actionLoading) {
  if (actionLoading || !site) {
    return true;
  }
  if (
    TERMINAL_RESET_ACTION_IDS.has(action.id) &&
    isTerminalResetDisabledForSite(site)
  ) {
    return true;
  }
  if (
    action.id === CLEAR_HARDWARE_ACTION_ID &&
    !isClearHardwareEnabledForSite(site)
  ) {
    return true;
  }
  if (
    action.id === CLEAR_THRESHOLD_EXCEEDED_ACTION_ID &&
    isThresholdExceededDisabledForSite(site)
  ) {
    return true;
  }
  if (action.requiresEsConnect && !getEsConnectTerminalId(terminal)) {
    return true;
  }
  if (action.requiresTerminalOid && !getTerminalOid(terminal)) {
    return true;
  }
  return false;
}

const TerminalActionCard = ({
  action,
  terminal,
  site,
  actionLoading,
  onRun,
}) => {
  const ActionIcon = action.icon;
  const disabled = isTerminalActionDisabled(
    action,
    terminal,
    site,
    actionLoading,
  );

  return (
    <ButtonBase
      onClick={() => onRun(action.id)}
      disabled={disabled}
      focusRipple={!disabled}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        borderRadius: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Paper
        variant='outlined'
        sx={{
          minHeight: 64,
          p: '10px 14px',
          borderRadius: '10px',
          width: '100%',
          borderColor: disabled ? 'rgba(5, 5, 51, 0.1)' : brand.borderSubtle,
          backgroundColor: disabled ? 'rgba(5, 5, 51, 0.04)' : '#FFFFFF',
          boxShadow: disabled ? 'none' : '0 1px 4px rgba(5, 5, 51, 0.06)',
          opacity: disabled ? 0.72 : 1,
          filter: disabled ? 'grayscale(1)' : 'none',
          transition:
            'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          ...(!disabled && {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 16px rgba(5, 5, 51, 0.12)',
              borderColor: action.iconColor,
              '& .terminal-action-chevron': {
                opacity: 0.85,
              },
            },
          }),
        }}
      >
        <Stack direction='row' spacing={1.25} alignItems='center'>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              bgcolor: disabled ? 'rgba(5, 5, 51, 0.08)' : action.avatarBg,
            }}
          >
            <ActionIcon
              sx={{
                fontSize: 20,
                color: disabled ? 'rgba(5, 5, 51, 0.32)' : action.iconColor,
              }}
            />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              spacing={0.5}
            >
              <Typography
                noWrap
                sx={{
                  fontFamily: 'BrightstarSans-Bold',
                  fontSize: '16px',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: disabled ? 'rgba(5, 5, 51, 0.38)' : brand.reachBlue,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {action.label}
              </Typography>
              <ChevronRightIcon
                className='terminal-action-chevron'
                sx={{
                  fontSize: 16,
                  color: disabled ? 'rgba(5, 5, 51, 0.32)' : 'text.secondary',
                  flexShrink: 0,
                  opacity: 0.4,
                  transition: 'opacity 0.2s ease',
                }}
              />
            </Stack>
            <Typography
              noWrap
              sx={{
                display: 'block',
                mt: 0.35,
                fontSize: '12px',
                lineHeight: 1.3,
                color: disabled ? 'rgba(5, 5, 51, 0.38)' : 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {action.description}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </ButtonBase>
  );
};

const TerminalActionsPanel = ({
  terminal,
  site,
  actionLoading,
  onRequestAction,
}) => {
  const actionsByGroup = {};

  for (let i = 0; i < TERMINAL_ACTION_DEFINITIONS.length; i++) {
    const action = TERMINAL_ACTION_DEFINITIONS[i];
    if (!actionsByGroup[action.group]) {
      actionsByGroup[action.group] = [];
    }
    actionsByGroup[action.group].push(action);
  }

  return (
    <Box>
      {TERMINAL_ACTION_GROUP_ORDER.map((groupName, groupIndex) => {
        const groupActions = actionsByGroup[groupName];
        if (!groupActions || groupActions.length === 0) {
          return null;
        }

        return (
          <Box
            key={groupName}
            sx={{
              mb:
                groupIndex < TERMINAL_ACTION_GROUP_ORDER.length - 1 ? 1.25 : 0,
            }}
          >
            <Typography
              sx={{
                display: 'block',
                mb: 0.5,
                fontSize: '13px',
                color: brand.reachBlue,
                fontFamily: 'BrightstarSans-Bold',
                fontWeight: 700,
                letterSpacing: '0.06em',
                lineHeight: 1.3,
                textTransform: 'uppercase',
              }}
            >
              {groupName}
            </Typography>
            <Grid container spacing={1.25}>
              {groupActions.map(action => (
                <Grid key={action.id} size={{ xs: 12, md: 6 }}>
                  <TerminalActionCard
                    action={action}
                    terminal={terminal}
                    site={site}
                    actionLoading={actionLoading}
                    onRun={onRequestAction}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default function TerminalModal({
  open,
  onClose,
  terminal,
  retailerId,
  site,
  onActionSuccess,
  setSnackbarMessage,
  setSnackbarSeverity,
  setIsSnackbarOpen,
  user,
}) {
  const [confirmType, setConfirmType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [signOnStatus, setSignOnStatus] = useState(null);
  const [signOnStatusLoading, setSignOnStatusLoading] = useState(false);
  const [downloadState, setDownloadState] = useState(null);
  const [downloadStateLoading, setDownloadStateLoading] = useState(false);
  const [lastTenTransactions, setLastTenTransactions] = useState([]);
  const [lastTenTransactionsLoading, setLastTenTransactionsLoading] =
    useState(false);
  const [lastTenTransactionsError, setLastTenTransactionsError] =
    useState(null);
  const [terminalHistory, setTerminalHistory] = useState([]);
  const [terminalHistoryLoading, setTerminalHistoryLoading] = useState(false);
  const trackTerminalAction = useTrackEvent(appInsights, 'TerminalAction');

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setSignOnStatus(null);
      setDownloadState(null);
      setLastTenTransactions([]);
      setLastTenTransactionsError(null);
      setTerminalHistory([]);
    }
  }, [open]);

  useEffect(() => {
    setActiveTab(0);
  }, [terminal]);

  const loadSignOnStatus = useCallback(async () => {
    if (!terminal || !site) {
      setSignOnStatus(null);
      return;
    }

    const terminalOid = getTerminalOid(terminal);
    setSignOnStatusLoading(true);
    try {
      const status = await getSignOnStatus({ terminalOid, site });
      setSignOnStatus(status);
    } catch (error) {
      console.warn('signOnStatus request failed:', error);
      setSignOnStatus(null);
    } finally {
      setSignOnStatusLoading(false);
    }
  }, [terminal, site]);

  const loadDownloadStatus = useCallback(async () => {
    if (!terminal || !site) {
      setDownloadState(null);
      return;
    }

    const esConnectTerminalId = getEsConnectTerminalId(terminal);
    setDownloadStateLoading(true);
    try {
      const status = await getDownloadStatus({ esConnectTerminalId, site });
      setDownloadState(status);
    } catch (error) {
      console.warn('downloadStatus request failed:', error);
      setDownloadState(null);
    } finally {
      setDownloadStateLoading(false);
    }
  }, [terminal, site]);

  const loadLastTenTransactions = useCallback(async () => {
    if (!terminal || !site) {
      setLastTenTransactions([]);
      setLastTenTransactionsError(null);
      return;
    }

    const terminalOid = getTerminalOid(terminal);
    setLastTenTransactionsLoading(true);
    setLastTenTransactionsError(null);
    try {
      const transactions = await getLastTenTransactions({ terminalOid, site });
      setLastTenTransactions(transactions);
    } catch (error) {
      console.warn('[lastTenTransactions] request failed:', error);
      setLastTenTransactions([]);
      setLastTenTransactionsError(
        'Unable to load transactions for this terminal.',
      );
    } finally {
      setLastTenTransactionsLoading(false);
    }
  }, [terminal, site]);

  const loadTerminalHistory = useCallback(async () => {
    if (!terminal || !site) {
      setTerminalHistory([]);
      return;
    }

    const terminalId = terminal.terminalId;
    setTerminalHistoryLoading(true);
    try {
      const history = await getTerminalHistoryFromApi({ terminalId, site });
      setTerminalHistory(history);
    } catch (error) {
      console.warn('[terminalHistory] request failed:', error);
      setTerminalHistory([]);
    } finally {
      setTerminalHistoryLoading(false);
    }
  }, [terminal, site]);

  useEffect(() => {
    if (!open || !terminal || !site) {
      return;
    }

    loadDownloadStatus();
    loadSignOnStatus();
  }, [open, terminal, site, loadDownloadStatus, loadSignOnStatus]);

  if (!terminal) return null;

  const terminalDisplayId = getTerminalDisplayId(terminal);
  const esConnectId = getEsConnectTerminalId(terminal);
  const statusMeta = getStatusChipMeta(getHostEntityStatusCode(terminal));
  const siteDisplay = formatSiteDisplay(site)?.toLocaleLowerCase();

  const trackTerminalActionSafe = (actionDef, response) => {
    try {
      trackTerminalAction({
        user,
        actionName: actionDef.id,
        params: {
          site,
          terminal,
        },
        response,
      });
    } catch (error) {
      console.warn('Telemetry trackTerminalAction failed:', error);
    }
  };

  const handleConfirm = async () => {
    const actionDef = TERMINAL_ACTION_DEFINITIONS.find(
      action => action.id === confirmType,
    );
    if (!actionDef) {
      return;
    }

    if (
      TERMINAL_RESET_ACTION_IDS.has(actionDef.id) &&
      isTerminalResetDisabledForSite(site)
    ) {
      setSnackbarMessage(
        'Soft and Hard Reset are not available for this jurisdiction.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      return;
    }

    if (
      actionDef.id === CLEAR_HARDWARE_ACTION_ID &&
      !isClearHardwareEnabledForSite(site)
    ) {
      setSnackbarMessage(
        'Clear Hardware ID is not available for this jurisdiction.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      return;
    }

    if (
      actionDef.id === CLEAR_THRESHOLD_EXCEEDED_ACTION_ID &&
      isThresholdExceededDisabledForSite(site)
    ) {
      setSnackbarMessage(
        'Clear Threshold Exceeded is not available for this jurisdiction.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      return;
    }

    setActionLoading(true);
    try {
      const response = await runTerminalAction(actionDef.id, site, terminal);
      trackTerminalActionSafe(actionDef, response);
      setSnackbarMessage(actionDef.success);
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setConfirmType(null);

      if (onActionSuccess) {
        await onActionSuccess();
      }

      await Promise.all([loadDownloadStatus(), loadSignOnStatus()]);
      if (activeTab === 2) {
        await loadTerminalHistory();
      }
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || actionDef.error;
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmConfig = confirmType
    ? TERMINAL_ACTION_DEFINITIONS.find(action => action.id === confirmType)
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='md'
        fullWidth
        aria-labelledby='terminal-details-title'
      >
        <DialogTitle
          id='terminal-details-title'
          component='div'
          sx={{ pr: 6, py: 2 }}
        >
          <Typography
            variant='h6'
            sx={{ fontFamily: 'BrightstarSans-Bold', lineHeight: 1.3 }}
          >
            Terminal Details
          </Typography>
          <Stack
            direction='row'
            alignItems='center'
            flexWrap='wrap'
            useFlexGap
            sx={{ mt: 1, gap: 0.75 }}
          >
            <Chip
              size='small'
              label={statusMeta.label}
              variant={statusMeta.chipVariant}
              sx={statusMeta.chipSx}
            />
            {siteDisplay && (
              <Chip
                size='small'
                label={siteDisplay}
                variant='outlined'
                sx={{
                  fontWeight: 600,
                  borderColor: brand.reachBlue,
                  color: brand.reachBlue,
                }}
              />
            )}
          </Stack>
          <IconButton
            aria-label='close'
            onClick={onClose}
            disabled={actionLoading}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 0, pb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(event, newTab) => {
              setActiveTab(newTab);
              if (newTab === 1) {
                loadLastTenTransactions();
              } else if (newTab === 2) {
                loadTerminalHistory();
              }
            }}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 1.5,
              minHeight: 40,
              '& .MuiTab-root': {
                fontFamily: 'BrightstarSans-Bold',
                textTransform: 'none',
                minHeight: 40,
                py: 1,
              },
            }}
          >
            <Tab label='Info' />
            <Tab label='Last 10 Trans.' />
            <Tab label='History' />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Paper
                variant='outlined'
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  borderColor: brand.borderSubtle,
                  backgroundColor: 'rgba(5, 10, 48, 0.02)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 1.5, md: 0 }}
                  divider={
                    <Divider
                      orientation='vertical'
                      flexItem
                      sx={{ display: { xs: 'none', md: 'block' }, mx: 2 }}
                    />
                  }
                  sx={{ alignItems: 'stretch' }}
                >
                  <Box sx={{ flex: { md: '6 1 0' }, minWidth: 0 }}>
                    <InfoSectionTitle>Terminal Information</InfoSectionTitle>
                    <Stack spacing={0.75}>
                      <DetailRow label='Retailer ID' value={retailerId} />
                      <DetailRow
                        label='Terminal Type'
                        value={terminal.description}
                      />
                      <DetailRow
                        label='Terminal ID'
                        value={terminal.terminalId}
                      />
                      <DetailRow
                        label='esConnect Terminal ID'
                        value={esConnectId}
                      />
                      <DetailRow
                        label='Terminal OID'
                        value={terminal.terminalOid}
                      />
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      flex: { md: '4 1 0' },
                      minWidth: 0,
                      pt: { xs: 1.5, md: 0 },
                      pl: { md: 1 },
                      borderTop: {
                        xs: `1px solid ${brand.borderSubtle}`,
                        md: 'none',
                      },
                    }}
                  >
                    <InfoSectionTitle>Sign-On Activity</InfoSectionTitle>
                    <SignOnActivitySection
                      signOnStatus={signOnStatus}
                      loading={signOnStatusLoading}
                      downloadState={downloadState}
                      downloadStateLoading={downloadStateLoading}
                    />
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant='outlined'
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.04)'
                      : 'rgba(5, 10, 48, 0.03)',
                }}
              >
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontFamily: 'BrightstarSans-Bold',
                    color: brand.reachBlue,
                    fontSize: '0.95rem',
                    mb: 0.25,
                    lineHeight: 1.3,
                  }}
                >
                  Terminal Actions
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ fontSize: '0.75rem', lineHeight: 1.35 }}
                >
                  These actions affect the selected terminal and require
                  confirmation.
                </Typography>

                <Box sx={{ mt: 1.25 }}>
                  <TerminalActionsPanel
                    terminal={terminal}
                    site={site}
                    actionLoading={actionLoading}
                    onRequestAction={setConfirmType}
                  />
                </Box>
              </Paper>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ height: 560, minHeight: 560, overflow: 'hidden' }}>
              <LastTenTransactionsTab
                transactions={lastTenTransactions}
                loading={lastTenTransactionsLoading}
                error={lastTenTransactionsError}
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ height: 560, minHeight: 560, overflow: 'hidden' }}>
              <TerminalHistoryTab
                historyEntries={terminalHistory}
                loading={terminalHistoryLoading}
                terminalDisplayId={terminalDisplayId}
                actionDefinitions={TERMINAL_ACTION_DEFINITIONS}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {confirmConfig && (
        <ConfirmDialog
          open={Boolean(confirmType)}
          title={confirmConfig.confirmTitle}
          messageParts={confirmConfig.getMessageParts(terminalDisplayId)}
          confirmVariant={confirmConfig.confirmVariant}
          onConfirm={handleConfirm}
          onCancel={() => !actionLoading && setConfirmType(null)}
          loading={actionLoading}
          confirmColor={confirmConfig.confirmMuiColor}
          confirmLabel={confirmConfig.confirmLabel}
          loadingLabel={confirmConfig.loadingLabel}
          confirmButtonSx={confirmConfig.confirmSx}
        />
      )}
    </>
  );
}
